export interface PriceBreakdownData {
  baseFare: number
  distanceFare: number
  timeFare: number
  weightAdjustment: number
  lodgeSurcharge: number
  fuelCost: number
  tollCost: number
  urgencyAmount: number
  specialRequirementsCost: number
  stopsCost: number
  surgeAmount: number
  platformFee: number
  taxes: number
  totalBeforeFees: number
  totalAfterFees: number
  currency: string
}

const LINE_KEYS: (keyof PriceBreakdownData)[] = [
  'baseFare',
  'distanceFare',
  'timeFare',
  'weightAdjustment',
  'lodgeSurcharge',
  'fuelCost',
  'tollCost',
  'urgencyAmount',
  'specialRequirementsCost',
  'stopsCost',
  'surgeAmount',
  'platformFee',
  'taxes',
  'totalBeforeFees',
  'totalAfterFees',
]

const PRICE_BREAKDOWN_COLUMN_KEYS = new Set([
  'initialestimatedprice',
  'finalprice',
  'estimatedpricebreakdown',
  'pricebreakdown',
  'offeredprice',
])

function toNum(value: unknown): number {
  if (value == null || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export function isPriceBreakdownColumnKey(key: string): boolean {
  const normalized = key.replace(/[_-]/g, '').toLowerCase()
  return PRICE_BREAKDOWN_COLUMN_KEYS.has(normalized)
}

export function isPriceBreakdown(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const obj = value as Record<string, unknown>
  return LINE_KEYS.some((key) => key in obj)
}

export function shouldIncludeTableColumn(
  key: string,
  value: unknown,
  excludeKeys: string[] = []
): boolean {
  if (excludeKeys.includes(key)) return false
  if (key.includes('password') || key.includes('token')) return false
  if (value === null || Array.isArray(value)) return true
  if (typeof value !== 'object') return true
  return isPriceBreakdown(value) || isPriceBreakdownColumnKey(key)
}

export function normalizePriceBreakdown(
  value: unknown,
  fallbackCurrency?: string | null
): PriceBreakdownData | null {
  if (!isPriceBreakdown(value)) return null
  const raw = value as Record<string, unknown>
  return {
    baseFare: toNum(raw.baseFare),
    distanceFare: toNum(raw.distanceFare),
    timeFare: toNum(raw.timeFare),
    weightAdjustment: toNum(raw.weightAdjustment),
    lodgeSurcharge: toNum(raw.lodgeSurcharge),
    fuelCost: toNum(raw.fuelCost),
    tollCost: toNum(raw.tollCost),
    urgencyAmount: toNum(raw.urgencyAmount),
    specialRequirementsCost: toNum(raw.specialRequirementsCost),
    stopsCost: toNum(raw.stopsCost),
    surgeAmount: toNum(raw.surgeAmount),
    platformFee: toNum(raw.platformFee),
    taxes: toNum(raw.taxes),
    totalBeforeFees: toNum(raw.totalBeforeFees),
    totalAfterFees: toNum(raw.totalAfterFees),
    currency:
      typeof raw.currency === 'string'
        ? raw.currency
        : String(fallbackCurrency || 'ILS'),
  }
}

export function extractJobOfferPriceBreakdown(
  offer: unknown,
  fallbackCurrency?: string | null
): PriceBreakdownData | null {
  if (!offer || typeof offer !== 'object' || Array.isArray(offer)) return null
  const raw = offer as Record<string, unknown>
  const currency = (raw.currency as string | undefined) || fallbackCurrency
  const candidates = [raw.priceBreakDown, raw.priceBreakdown, raw.offeredPrice, raw.price, raw]
  for (const value of candidates) {
    const breakdown = normalizePriceBreakdown(value, currency)
    if (breakdown) return breakdown
  }
  return null
}

export function formatBreakdownSummary(
  breakdown: PriceBreakdownData,
  getCurrencyLabel?: (code: string) => string
): string {
  const total = breakdown.totalAfterFees || breakdown.totalBeforeFees
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: breakdown.currency || 'ILS',
      maximumFractionDigits: 2,
    }).format(total)
  } catch {
    const symbol = getCurrencyLabel?.(breakdown.currency) || breakdown.currency
    return `${total.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${symbol}`.trim()
  }
}

export type PriceBreakdownLineCategory = 'fare' | 'adjustment' | 'fee' | 'total'

export interface PriceBreakdownLineItem {
  key: keyof PriceBreakdownData
  label: string
  category: PriceBreakdownLineCategory
}

export const PRICE_BREAKDOWN_LINE_ITEMS: PriceBreakdownLineItem[] = [
  { key: 'baseFare', label: 'Base Fare', category: 'fare' },
  { key: 'distanceFare', label: 'Distance Fare', category: 'fare' },
  { key: 'timeFare', label: 'Time Fare', category: 'fare' },
  { key: 'weightAdjustment', label: 'Weight Adjustment', category: 'adjustment' },
  { key: 'lodgeSurcharge', label: 'Lodge Surcharge', category: 'adjustment' },
  { key: 'urgencyAmount', label: 'Urgency Amount', category: 'adjustment' },
  { key: 'surgeAmount', label: 'Surge Amount', category: 'adjustment' },
  { key: 'specialRequirementsCost', label: 'Special Requirements', category: 'adjustment' },
  { key: 'stopsCost', label: 'Stops Cost', category: 'adjustment' },
  { key: 'fuelCost', label: 'Fuel Cost', category: 'fee' },
  { key: 'tollCost', label: 'Toll Cost', category: 'fee' },
  { key: 'platformFee', label: 'Platform Fee', category: 'fee' },
  { key: 'taxes', label: 'Taxes', category: 'fee' },
]

export function getPriceBreakdownLineColor(
  category: PriceBreakdownLineCategory,
  primary: string,
  warning: string,
  mode: 'light' | 'dark'
): string {
  switch (category) {
    case 'fare':
      return primary
    case 'adjustment':
      return warning
    case 'fee':
      return mode === 'light' ? '#7B1FA2' : '#CE93D8'
    default:
      return primary
  }
}
